export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'devutils:theme';

function resolveSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function readStoredTheme(): Theme {
  if (typeof localStorage === 'undefined') return 'dark';
  const value = localStorage.getItem(STORAGE_KEY);
  if (value === 'light' || value === 'dark' || value === 'system') {
    return value;
  }
  return 'dark';
}

/**
 * Title-bar colors: must match --background in src/styles/globals.css so the
 * Chrome side-panel title bar visually merges with our content (no seam).
 *   dark  → hsl(240 10% 4%)  = #09090b
 *   light → hsl(0   0% 100%) = #ffffff
 */
const THEME_COLORS: Record<'light' | 'dark', string> = {
  dark: '#09090b',
  light: '#ffffff',
};

function syncThemeColorMeta(effective: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  let meta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]',
  );
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = THEME_COLORS[effective];
}

export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  const effective = theme === 'system' ? resolveSystemTheme() : theme;
  root.classList.toggle('dark', effective === 'dark');
  root.classList.toggle('light', effective === 'light');
  root.style.colorScheme = effective;
  syncThemeColorMeta(effective);
}

export function setTheme(theme: Theme) {
  localStorage.setItem(STORAGE_KEY, theme);
  applyTheme(theme);
}

export function initTheme() {
  applyTheme(readStoredTheme());
  if (typeof window !== 'undefined' && window.matchMedia) {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', () => {
      if (readStoredTheme() === 'system') applyTheme('system');
    });
  }
}
